import { ChangeEvent, useEffect, useState } from "react"

import { cn, getQuestionText, HOMEWORK_STATE } from "@/lib/utils"
import { Chat } from "@/components/ui/chat"
import { SWRConfig } from "swr"
import { askChatbot } from "@/services/generatedAPIs"
import { Message } from "@/components/ui/chat-message"
import { useLocation, useNavigate } from "react-router"
import { QuestionModel } from "@/services/generatedAPIs.schemas"
import { useAuth } from "@/contexts/AuthContext"
import { useTranslation } from "react-i18next"
import { ChevronLeft } from "lucide-react"
import { PromptButtonsMessage } from "@/components/ui/prompt-buttons-message"
import { useAsyncAction } from "@/hooks/use-async-action"
import ContentLayout from "@/components/layout/ContentLayout"
import MobileLandscapeAITutor from "@/components/mobile-landscape-ai-tutor"

type AITutorPageProps = {
}

function ChatComponent(props: AITutorPageProps) {
    const { getCurrentUser } = useAuth();
    const navigate = useNavigate();
    const state = useLocation().state as { questionModel?: QuestionModel; assignmentId?: number; tabId?: string; itemId?: number };
    let [messages, setMessages] = useState<Array<Message>>([])
    // let [input, setInput] = useState<string>(state?.questionModel ? `問題：${state.questionModel.questionInfo.questionText}。` + (state.questionModel.question?.answer ? ("回答：" + state.questionModel.question?.answer) : "怎麼解題？") : "")
    let [input, setInput] = useState<string>("")
    let { isLoading, execute } = useAsyncAction({ showErrorToast: false })
    let [firstContent, setFirstContent] = useState("");
    const [sessionId] = useState(() => Date.now().toString());
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if ((state && state.questionModel)) {
            // handleSubmit(undefined);
            let msg = Array.from(messages);
            let newContent = t('student.botopening').replace('%s', getQuestionText(state.questionModel.questionInfo, i18n)).replace('%s', (state.questionModel.question?.answer ? state.questionModel.question?.answer : ""));
            firstContent = newContent;
            setFirstContent(newContent);
            msg.push({
                id: "1",
                role: "assistant",
                content: newContent
            })
            setMessages(msg);
        }
    }, []);

    const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setInput(event.target.value);
    }

    const handleSubmit = (event) => {
        event?.preventDefault();
        if (input) {
            let isFirstSubmit = messages.length === 1;
            let msg = Array.from(messages);
            msg.push({
                id: "1",
                role: "user",
                content: input
            })
            setMessages(msg);
            messages = msg;
            execute(async () => {
                try {
                    const rsp = await askChatbot({ chatId: getCurrentUser()!.id.toString() + ":" + state?.questionModel?.question?.id + ":" + sessionId, message: (isFirstSubmit ? firstContent : "") + input, questionId: state?.questionModel?.question?.id, assignmentId: state?.assignmentId });
                    let msg = Array.from(messages);
                    msg.push({
                        id: "1",
                        role: "assistant",
                        content: rsp.data!.message
                    })
                    setMessages(msg);
                } catch (error) {
                    console.error("Error generating response:", error);
                    let msg = Array.from(messages);
                    msg.push({
                        id: "1",
                        role: "assistant",
                        content: "Error: " + (error as Error).message
                    })
                    setMessages(msg);
                }
            });
            setInput("");
            input = "";
        }
    }

    const handlePromptSelect = (prompt: string) => {
        setInput(prompt);
    }

    return (
        <div className={cn("flex", "flex-col", "w-full", "flex-1", "min-h-0")}>
            {
                state && state.questionModel &&
                <div className="flex flex-row items-center gap-1 pb-2 cursor-pointer text-foreground/70 transition-colors hover:text-foreground" onClick={() => {
                    // Restore homework state before navigating back
                    if (state?.tabId) {
                        HOMEWORK_STATE.tabId = state.tabId;
                    }
                    if (state?.itemId !== undefined) {
                        HOMEWORK_STATE.itemId = state.itemId;
                    }
                    navigate(-1);
                }}>
                    <ChevronLeft size={18}></ChevronLeft>
                    <span className="text-sm">{t('component.backtoquiz')}</span>
                </div>
            }
            <Chat
                className="grow min-h-0 bg-[#F3F3F4] p-6 lg:p-8 rounded-[28px] xl:rounded-[35px] 2xl:rounded-[40px] min-[1920px]:rounded-[50px]!"
                placeholder={t('student.aiPlaceholder')}
                messages={messages}
                suggestions={[t('student.botchat1'), t('student.botchat2'), t('student.botchat3')]}
                append={(message) => {
                    input += message.content;
                    setInput(input);
                    handleSubmit(undefined);
                }}
                input={input}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                isGenerating={isLoading}
                stop={() => { }}
                promptButtons={
                    state && state.questionModel ? (
                        <PromptButtonsMessage
                            // label={t('student.botoptionslabel')}
                            suggestions={[
                                t('student.botoption1'),
                                t('student.botoption2'),
                                t('student.botoption3')
                            ]}
                            onSelectPrompt={handlePromptSelect}
                        />
                    ) : undefined
                }
            />
        </div>
    )
}

export default function AITutorPage(props: AITutorPageProps) {
    const { t } = useTranslation();
    return (
        <SWRConfig value={{ fetcher: undefined }}>
            {/* 桌面/平板:原样;手机横屏(高≤500)隐藏,改用下方独立舞台 */}
            <ContentLayout title={t('component.exttitle')} backgroundColor="var(--practice-page-bg)" className="[@media(max-height:500px)]:hidden">
                {/* lg/xl 左 padding 收到 pl-0(灰板贴近侧栏,gap≈24,同课业/练习页)；2xl 锚 pl-10 保持 1536/1920 不变 */}
                <div className="mx-auto flex w-full max-w-[2000px] flex-1 flex-col min-h-0 px-6 pt-6 pb-6 lg:pl-0 lg:pr-10 lg:pb-10 2xl:pl-10">
                    <ChatComponent></ChatComponent>
                </div>
            </ContentLayout>
            {/* 手机横屏(844×390)AI 提问页:自带门控(高>500 返回 null)+ createPortal 独立舞台 */}
            <MobileLandscapeAITutor />
        </SWRConfig>
    )
}